/**
 * The knob registry — what taste is allowed to be about.
 *
 * Every domain is drawn from the closed vocabulary in `@morph/spec`. If it is
 * not a knob, the model cannot hold an opinion about it, and it cannot be
 * rendered. That is the point: **a taste model that cannot be compiled into a
 * LayoutSpec is a bug, and this registry makes that statically checkable.**
 */

import {
  DENSITIES, TYPE_SYSTEMS, PALETTES, FLOWS,
  type Role, type Affordance, type CapabilityId,
} from '@morph/spec';

export const KNOBS = {
  density:           { domain: DENSITIES },
  typography:        { domain: TYPE_SYSTEMS },
  palette:           { domain: PALETTES },
  'collection.flow': { domain: FLOWS },
  'media.fit':       { domain: ['cover', 'contain'] },
  /** The most useful thing the model can learn: which metadata you actually
   *  want to see. `suppressed` means the generator may not bind it at all. */
  'role.presence':   { domain: ['prominent', 'present', 'suppressed'], indexed: true },
  /**
   * WITHDRAWN by decision 0036, and the empty space is the point.
   *
   * `role.extent` was registered with a closed domain bound to `EXTENTS`, it
   * type-checked, and it compiled to nothing: the schema carries `extent` only
   * on `docked`, `floating` and `anchored` placements, and the baseline
   * generator emits no placement at all, so every node it produces is
   * implicitly inline. @Codex: *"The registry is a capability contract, not a
   * catalogue of values that might become renderable later. A closed domain is
   * necessary for that contract but does not prove it."*
   *
   * The four conditions for its return are in the decision. The first two are
   * the ones this comment exists to keep honest: the generator must emit the
   * placement that owns the extent, and changing the taste value must change
   * the compiled `LayoutSpec` in a test, *"without relying on demo-only CSS or
   * post-render mutation"*.
   */
} as const satisfies Record<string, { domain: readonly string[]; indexed?: boolean }>;

export type KnobId = keyof typeof KNOBS;
export type KnobIndex = Role | Affordance | CapabilityId;

export const isKnob = (k: string): k is KnobId => k in KNOBS;
export const domainOf = (k: KnobId): readonly string[] => KNOBS[k].domain;
export const isIndexed = (k: KnobId): boolean => 'indexed' in KNOBS[k];

/**
 * Scope hierarchy. Prior strength *increases* as you descend, which is a churn
 * decision rather than a statistical one: making deep cells hard to diverge
 * means the user has **one look**. A flat model with independent cells would
 * produce a different layout per surface — churn distributed across space
 * instead of across time, which costs the same and is harder to see.
 */
export type ScopeLevel = 'global' | 'context' | 'surface';

export const PRIOR_MASS: Record<ScopeLevel, number> = {
  global: 4,    // four consistent signals override the pool's default
  context: 8,   // your reading taste diverges from your general taste slowly
  surface: 12,  // a specific shape cell almost never diverges
};

export interface Scope {
  level: ScopeLevel;
  context?: string;
  cardinality?: string;
  mediaWeight?: string;
}

export const scopeId = (s: Scope): string =>
  s.level === 'global' ? 'global'
  : s.level === 'context' ? `ctx:${s.context}`
  : `srf:${s.context}/${s.cardinality}/${s.mediaWeight}`;

export const claimKey = (knob: KnobId, scope: Scope, index?: string): string =>
  index ? `${knob}[${index}]@${scopeId(scope)}` : `${knob}@${scopeId(scope)}`;

/** Walk from a scope up to global. Each level is the prior for the one below. */
export function scopeChain(s: Scope): Scope[] {
  if (s.level === 'global') return [{ level: 'global' }];
  if (s.level === 'context') return [s, { level: 'global' }];
  const ctx: Scope = s.context !== undefined
    ? { level: 'context', context: s.context }
    : { level: 'context' };
  return [s, ctx, { level: 'global' }];
}
